import {useEffect} from 'react';

/**
 * Fixes MUI Dialog/Modal accessibility issues caused by `aria-hidden="true"`.
 *
 * MUI adds `aria-hidden` to `#root` and background modals when a dialog opens,
 * which can create WCAG/axe accessibility violations because focusable elements
 * still exist inside hidden containers.
 *
 * This utility removes those invalid `aria-hidden` attributes and keeps
 * modal accessibility behavior working correctly.
 *
 * @see https://github.com/mui/material-ui/issues/19450
 */

const ModalAriaHiddenFix = ({
  rootSelector = '#root',
}: {
  rootSelector?: string;
}): null => {
  useEffect(() => {
    const root = document.querySelector(rootSelector);

    const syncModals = () => {
      // remove aria-hidden from root
      if (root?.getAttribute('aria-hidden') === 'true') {
        root.removeAttribute('aria-hidden');
      }

      // remove aria-hidden from sibling modals
      document
        .querySelectorAll<HTMLElement>('.MuiModal-root[aria-hidden="true"]')
        .forEach(el => {
          el.removeAttribute('aria-hidden');
        });
    };

    syncModals();

    const observer = new MutationObserver(syncModals);

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });

    return () => observer.disconnect();
  }, [rootSelector]);

  return null;
};

export default ModalAriaHiddenFix;
